import { DateVO, LanguageVO, Nationalities } from '@zazume/zzm-utils';

import { TFunction } from '#/hooks/useI18n';
import { I18NValues } from '#/lib/i18n/generatedKeys';
import { DocumentMetadata } from '#/models';

interface FormattedDetails {
  field: string;
  value: string | number;
}

export class MetadataHelper {
  private readonly metadata: DocumentMetadata;

  constructor(metadata: DocumentMetadata) {
    this.metadata = metadata;
  }

  static build(metadata: DocumentMetadata) {
    return new MetadataHelper(metadata);
  }

  formatType(t: TFunction): string {
    return t(`documents.types.${this.metadata.type}`);
  }

  formatDetailsValue(t: TFunction, lang: LanguageVO, field: string, value: string | number): string {
    const normalizedValue = value.toString();

    if (this.isDateField(field)) {
      return this.formatDateValue(normalizedValue, lang);
    }
    if (this.isNationalityField(field)) {
      return Nationalities.getByCode(normalizedValue, lang);
    }

    return normalizedValue;
  }

  getFormattedData(t: TFunction, lang: LanguageVO): FormattedDetails[] {
    const formattedData = [...this.getFormattedDetail(t, lang)];
    const holderName = this.metadata.mainData.holder_name;
    if (holderName) {
      formattedData.unshift({
        field: t('documents.metadata.fields.holderName'),
        value: holderName as string
      });
    }
    const dateIssued = this.metadata.mainData.date_issued;
    if (dateIssued) {
      formattedData.unshift({
        field: t('documents.metadata.fields.dateIssued'),
        value: this.formatDateValue(dateIssued, lang)
      });
    }
    return formattedData;
  }

  getFormattedDetail(t: TFunction, lang: LanguageVO): FormattedDetails[] {
    const details = this.metadata.mainData?.details;
    if (!details) {
      return [];
    }
    return Object.entries(details).reduce<FormattedDetails[]>((acc, [key, value]) => {
      if (!value) {
        return acc;
      }
      const valueFormatted = this.formatDetailsValue(t, lang, key, value);
      if (!valueFormatted) {
        return acc;
      }
      return [
        ...acc,
        {
          field: t(`documents.metadata.fields.${key}` as I18NValues),
          value: valueFormatted
        }
      ];
    }, []);
  }

  private formatDateValue(value: string, lang: LanguageVO): string {
    return DateVO.fromString('YYYY-MM-DD', value).format('date', lang.toPlatform());
  }

  private isDateField(fieldName: string): boolean {
    return ['workStartDate', 'birthDate'].includes(fieldName);
  }

  private isNationalityField(fieldName: string): boolean {
    return fieldName === 'nationality';
  }
}
